import React, { forwardRef } from "react";
import { CpIcon, CpButton } from "@components";
import { Icons } from "@types";
import { always, tw, maybe } from "src/utils";
import { intentStyles } from "./variants/cp-inline-notification-styles";
import {
  typeToIntentMap,
  CpInlineNotificationIntent,
  defaultIcons,
  iconColorTokens,
} from "./variants/cp-inline-notification.helper";

type NotificationType = "info" | "warning" | "error";

type CpInlineNotificationProps = {
  message: string | React.ReactNode;
  cta?: React.ReactNode;
  /** @deprecated Use intent prop instead. Maps: info->brand, warning->critical (for now), error->critical */
  type?: NotificationType;
  intent?: CpInlineNotificationIntent;
  icon?: Icons;
  /** Callback when close button is clicked. If provided, a close button will be rendered. */
  onClose?: () => void;
  /**
   * Set when notification is inset within a container (removes rounded corners to fit flush).
   * Users can manually add overflow: hidden to parent containers or specific corner radius styles as needed.
   */
  inset?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export const CpInlineNotification = forwardRef<
  HTMLDivElement,
  CpInlineNotificationProps
>(function CpInlineNotification(props, ref) {
  const {
    message,
    cta,
    type,
    intent: intentProp,
    icon,
    onClose,
    inset,
    className,
    ...rest
  } = props;

  // Handle legacy type prop with deprecation warning
  let finalIntent: CpInlineNotificationIntent = intentProp || "brand";
  if (type && !intentProp) {
    console.warn(
      `CpInlineNotification: "type" prop is deprecated. Use "intent" instead. Mapping type="${type}" to intent="${typeToIntentMap[type]}"`,
    );
    finalIntent = typeToIntentMap[type];
  }

  // Determine icon: use provided icon, or default based on intent
  const finalIcon = icon || defaultIcons[finalIntent];

  return (
    <div
      {...rest}
      ref={ref}
      className={always(
        tw(
          "flex items-center gap-4 p-4 text-sm",
          intentStyles[finalIntent],
          maybe(!inset, "rounded-lg"),
        ),
        className,
      )}
    >
      <CpIcon
        name={finalIcon}
        fill={iconColorTokens[finalIntent]}
        className={tw("flex-shrink-0")}
      />
      <div className={tw("flex-grow")}>{message}</div>
      {!!cta && <div className={tw("flex-shrink-0")}>{cta}</div>}
      {!!onClose && (
        <CpButton
          icon="close-small"
          fill={iconColorTokens[finalIntent]}
          onClick={onClose}
          aria-label="Close notification"
          className={tw("flex-shrink-0")}
        />
      )}
    </div>
  );
});
